import { useCallback, useEffect, useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { Group, Panel, Separator } from 'react-resizable-panels';
import {
  usePreisToolItems,
  usePreisToolFacets,
  usePreisToolLegendThresholds,
  usePreisToolPriceChangeBatches,
  usePreisToolPriceWritebackStatus,
  usePreisToolSupplierAnomalies,
  useRefreshAfterTransfer,
  useSavePreisToolPrices,
} from '@/api/preis-tool/preis-tool.hooks';
import type {
  PreisToolItem,
  PreisToolPriceChangeResult,
  PreisToolSapWritebackMode,
  PreisToolSaveRefusal,
  PreisToolSortField,
  SavePreisToolPriceChange,
} from '@/api/preis-tool/preis-tool.types';
import { useGridState } from '@hooks/use-grid-state';
import { usePermissions } from '@/hooks/use-permissions';
import { PERMISSIONS } from '@/constants/permissions';
import { DsButton, DsModal } from '@/modules/internal-v2/ds';
import {
  PRICE_FIELDS,
  computeBedarf,
  fmtEUR,
  fmtPct,
  marginPct,
  marginClass,
  channelLabel,
  writebackHint,
  type PriceField,
} from './helpers';
import {
  countRowStates,
  DEFAULT_LEGEND_LIMITS,
  filterByRowStates,
  rowStatesById,
  sanitizeRowStates,
  type LegendLimits,
  type RowState,
} from './row-states';
import { FilterToolbar } from './components/filter-panel.component';
import { LegendFilter } from './components/legend-filter.component';
import { HitList, type SortingState } from './components/hit-list.component';
import type { PriceDrafts, ActiveCell } from './components/hit-list-columns';
import { Workspace } from './components/workspace.component';
import { RightRail } from './components/right-rail.component';
import { PriceListImportDialog } from './components/price-list-import-dialog.component';
import './preis-tool.css';

const SORT_FIELD_MAP: Record<string, PreisToolSortField> = {
  id: 'id',
  name: 'name',
  manufacturerName: 'manufacturerName',
  movingAveragePrice: 'movingAveragePrice',
  standardPrice: 'standardPrice',
  itscopePrice: 'itscopePrice',
  onlinerPrice: 'onlinerPrice',
  quantityOnStock: 'quantityOnStock',
  quantityOrderedByCustomers: 'quantityOrderedByCustomers',
  quantityOrderedFromVendors: 'quantityOrderedFromVendors',
  minInventory: 'minInventory',
  desiredInventory: 'desiredInventory',
};

/** One save takes at most this many prices; more drafts go out as several saves. */
const SAVE_CHUNK_SIZE = 500;

/** Stable fallback while no legend state is selected. */
const NO_LEGEND_STATES: RowState[] = [];

export function PreisToolPage() {
  const { has } = usePermissions();
  const canEditPrices = has(PERMISSIONS.ITEMS_EDIT_PRICES);
  // Purchase-side data (fixed prices, offers, upload, reorder point) has its own right.
  const canManage = has(PERMISSIONS.PREIS_TOOL_MANAGE);
  const { getCustom, setCustom } = useGridState('preis-tool-v2');

  const query = getCustom<string>('query', '');
  const setQuery = useCallback((v: string) => setCustom('query', v), [setCustom]);
  const selectedMfgs = getCustom<number[]>('selectedMfgs', []);
  const setSelectedMfgs = useCallback((v: number[]) => setCustom('selectedMfgs', v), [setCustom]);
  const selectedGroups = getCustom<number[]>('selectedGroups', []);
  const setSelectedGroups = useCallback((v: number[]) => setCustom('selectedGroups', v), [setCustom]);
  const onlyDemand = getCustom<boolean>('onlyDemand', false);
  const setOnlyDemand = useCallback((v: boolean) => setCustom('onlyDemand', v), [setCustom]);
  const onlyAnomalies = getCustom<boolean>('onlyAnomalies', false);
  const setOnlyAnomalies = useCallback((v: boolean) => setCustom('onlyAnomalies', v), [setCustom]);
  const priceFilter = getCustom<'priced' | 'all' | 'unpriced'>('priceFilter', 'all');
  const setPriceFilter = useCallback((v: 'priced' | 'all' | 'unpriced') => setCustom('priceFilter', v), [setCustom]);

  const onlyNotInSap = getCustom<boolean>('onlyNotInSap', false);
  const setOnlyNotInSap = useCallback((v: boolean) => setCustom('onlyNotInSap', v), [setCustom]);

  // Legend states the list is narrowed to (union). Page state: it stays when
  // another article is picked and when the user comes back to the page.
  const legendStatesRaw = getCustom<unknown>('legendStates', NO_LEGEND_STATES);
  const legendStates = useMemo(() => sanitizeRowStates(legendStatesRaw), [legendStatesRaw]);
  const toggleLegendState = useCallback(
    (state: RowState) =>
      setCustom(
        'legendStates',
        legendStates.includes(state) ? legendStates.filter((s) => s !== state) : [...legendStates, state],
      ),
    [legendStates, setCustom],
  );

  const [onlyEdited, setOnlyEdited] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'name', desc: false }]);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [basket, setBasket] = useState<string[]>([]);
  const [drafts, setDrafts] = useState<PriceDrafts>({});
  const [activeCell, setActiveCell] = useState<ActiveCell | null>(null);
  const [priceListImportOpen, setPriceListImportOpen] = useState(false);

  const { data: facets } = usePreisToolFacets();
  const { data: supplierAnomalies, isPending: anomaliesLoading } =
    usePreisToolSupplierAnomalies({ limit: 50 });
  const { data: legendThresholds } = usePreisToolLegendThresholds();
  const dealMinSavingsPct = legendThresholds?.dealMinSavingsPct ?? DEFAULT_LEGEND_LIMITS.dealMinSavingsPct;
  const lowMarginPct = legendThresholds?.lowMarginPct ?? DEFAULT_LEGEND_LIMITS.lowMarginPct;
  const legendLimits = useMemo<LegendLimits>(
    () => ({ dealMinSavingsPct, lowMarginPct }),
    [dealMinSavingsPct, lowMarginPct],
  );
  const { data: writeback } = usePreisToolPriceWritebackStatus();
  const savePrices = useSavePreisToolPrices();
  const refreshAfterTransfer = useRefreshAfterTransfer();
  const { enqueueSnackbar } = useSnackbar();

  // Saves whose prices are still on their way to SAP.
  const [trackedBatchIds, setTrackedBatchIds] = useState<string[]>([]);
  const trackedBatches = usePreisToolPriceChangeBatches(trackedBatchIds);
  useEffect(() => {
    trackedBatches.forEach((batch, i) => {
      const changes = batch.data?.changes;
      if (!changes || changes.some((c) => c.status === 'PENDING')) return;
      const batchId = trackedBatchIds[i];
      const transferred = changes.filter((c) => c.status === 'TRANSFERRED').length;
      const failed = changes.filter((c) => c.status === 'FAILED').length;
      setTrackedBatchIds((prev) => prev.filter((id) => id !== batchId));
      void refreshAfterTransfer();
      if (failed > 0) {
        enqueueSnackbar(
          `${failed} Preis${failed !== 1 ? 'e' : ''} nicht nach SAP übertragen${transferred > 0 ? `, ${transferred} übertragen` : ''}. Die Fehlermeldung steht am Preis.`,
          { variant: 'error' },
        );
      } else if (transferred > 0) {
        enqueueSnackbar(`${transferred} Preis${transferred !== 1 ? 'e' : ''} in SAP übertragen`, {
          variant: 'success',
        });
      }
    });
  }, [trackedBatches, trackedBatchIds, refreshAfterTransfer, enqueueSnackbar]);

  const anomalyItemIds = useMemo(
    () => new Set((supplierAnomalies?.entries ?? []).map((e) => e.itemId)),
    [supplierAnomalies],
  );

  const sortField = sorting[0] ? SORT_FIELD_MAP[sorting[0].id] : undefined;
  const sortOrder = sorting[0] ? (sorting[0].desc ? 'desc' : 'asc') : undefined;

  const { data, isPending } = usePreisToolItems({
    q: query || undefined,
    manufacturer: selectedMfgs.length ? selectedMfgs : undefined,
    itemGroup: selectedGroups.length ? selectedGroups : undefined,
    sortField,
    sortOrder: sortField ? sortOrder : undefined,
    limit: 10000,
    offset: 0,
  });

  const items = data?.data;

  // Legend states of every loaded row (EOS-61): row colour, deal dot, counts.
  const rowStates = useMemo(
    () => rowStatesById(items ?? [], legendLimits),
    [items, legendLimits],
  );

  // Every filter except the legend: the legend counts are taken from these rows.
  const baseRows = useMemo(() => {
    if (!items) return [];
    let filtered = items;

    if (priceFilter === 'priced') {
      filtered = filtered.filter((a) => a.standardPrice || a.itscopePrice || a.onlinerPrice);
    } else if (priceFilter === 'unpriced') {
      filtered = filtered.filter((a) => !a.standardPrice && !a.itscopePrice && !a.onlinerPrice);
    }

    if (onlyDemand) {
      filtered = filtered.filter((a) => computeBedarf(a) > 0);
    }

    if (onlyAnomalies) {
      filtered = filtered.filter((a) => anomalyItemIds.has(a.id));
    }

    if (onlyEdited) {
      filtered = filtered.filter((a) => drafts[a.id] && Object.keys(drafts[a.id]).length > 0);
    }

    if (onlyNotInSap) {
      filtered = filtered.filter((a) => a.openPriceChanges != null);
    }

    return filtered;
  }, [items, priceFilter, onlyDemand, onlyAnomalies, onlyEdited, onlyNotInSap, drafts, anomalyItemIds]);

  const legendCounts = useMemo(() => countRowStates(baseRows, rowStates), [baseRows, rowStates]);

  // Active legend states narrow the list to their union, on top of every other filter.
  const rows = useMemo(
    () => filterByRowStates(baseRows, rowStates, legendStates),
    [baseRows, rowStates, legendStates],
  );

  // Saved prices that are not in SAP yet, across the whole loaded list.
  const transferCounts = useMemo(() => {
    let pending = 0;
    let failed = 0;
    for (const item of data?.data ?? []) {
      for (const change of Object.values(item.openPriceChanges ?? {})) {
        if (change.status === 'FAILED') failed++;
        else pending++;
      }
    }
    return { pending, failed };
  }, [data?.data]);

  const selected = useMemo(
    () => rows.find((a) => a.id === selectedId),
    [rows, selectedId],
  );

  const onPriceCommit = useCallback((itemId: string, field: PriceField, value: number | null) => {
    if (!canEditPrices) return;
    const item = data?.data.find((a) => a.id === itemId);
    if (!item) return;
    const original = item[field];
    setDrafts((prev) => {
      const itemDrafts = { ...prev[itemId] };
      // An emptied or unreadable input is not a price: drop the draft instead
      // of carrying a change that cannot be saved.
      if (value === null || value === original) {
        delete itemDrafts[field];
      } else {
        itemDrafts[field] = value;
      }
      if (Object.keys(itemDrafts).length === 0) {
        const next = { ...prev };
        delete next[itemId];
        return next;
      }
      return { ...prev, [itemId]: itemDrafts };
    });
  }, [data?.data, canEditPrices]);

  const dirtyCount = useMemo(() => {
    let count = 0;
    for (const fields of Object.values(drafts)) {
      count += Object.keys(fields).length;
    }
    return count;
  }, [drafts]);

  const handleSave = async () => {
    const changes: SavePreisToolPriceChange[] = [];
    for (const [itemId, fields] of Object.entries(drafts)) {
      for (const field of PRICE_FIELDS) {
        const price = fields[field];
        if (price != null) changes.push({ itemId, field, price });
      }
    }
    if (changes.length === 0) return;

    // The backend refuses a whole save with a price of 0 or less; say why here.
    const notPositive = [...new Set(changes.filter((c) => !(c.price > 0)).map((c) => c.itemId))];
    if (notPositive.length > 0) {
      enqueueSnackbar(
        `Ein Verkaufspreis muss größer als 0 sein (${notPositive.join(', ')}). Es wurde nichts gespeichert, die Änderungen bleiben als Entwurf erhalten.`,
        { variant: 'error' },
      );
      return;
    }

    // Each save is taken whole or not at all.
    const results: PreisToolPriceChangeResult[] = [];
    let mode: PreisToolSapWritebackMode | undefined;
    let refusal: string | null = null;
    for (let i = 0; i < changes.length; i += SAVE_CHUNK_SIZE) {
      try {
        const response = await savePrices.mutateAsync({ changes: changes.slice(i, i + SAVE_CHUNK_SIZE) });
        results.push(...response.results);
        mode = response.sapWriteback;
        if (response.saved > 0 && response.sapWriteback !== 'disabled') {
          setTrackedBatchIds((prev) => [...prev, response.batchId]);
        }
      } catch (error) {
        const body = isAxiosError<PreisToolSaveRefusal>(error) ? error.response?.data : undefined;
        refusal = body?.message ?? 'Speichern fehlgeschlagen.';
        break;
      }
    }

    // Only what the backend took over leaves the drafts.
    setDrafts((prev) => {
      const next: PriceDrafts = { ...prev };
      for (const result of results) {
        if (!next[result.itemId]) continue;
        const itemDrafts = { ...next[result.itemId] };
        delete itemDrafts[result.field];
        if (Object.keys(itemDrafts).length === 0) delete next[result.itemId];
        else next[result.itemId] = itemDrafts;
      }
      return next;
    });

    const saved = results.length;
    if (refusal) {
      enqueueSnackbar(
        `${refusal}${saved > 0 ? ` ${saved} Preis${saved !== 1 ? 'e' : ''} wurde${saved !== 1 ? 'n' : ''} vorher gespeichert.` : ''} Die übrigen Änderungen bleiben als Entwurf erhalten.`,
        { variant: 'error' },
      );
    } else if (saved > 0) {
      enqueueSnackbar(
        `${saved} Preis${saved !== 1 ? 'e' : ''} gespeichert${mode === 'disabled' ? ' · noch nicht in SAP' : ' · Übertragung nach SAP läuft'}`,
        { variant: 'success' },
      );
    }
  };

  // A failed transfer: send the wanted price again or keep the SAP price.
  // Both are a new save of that one price; keeping the SAP price ends without
  // a write, because SAP holds it already.
  const resolveFailedPrice = async (itemId: string, field: PriceField, price: number, keepSapPrice: boolean) => {
    try {
      const response = await savePrices.mutateAsync({ changes: [{ itemId, field, price }] });
      if (response.saved > 0 && response.sapWriteback !== 'disabled') {
        setTrackedBatchIds((prev) => [...prev, response.batchId]);
      }
      enqueueSnackbar(
        keepSapPrice
          ? `SAP-Preis € ${fmtEUR(price)} wird behalten`
          : `€ ${fmtEUR(price)} wird erneut an SAP gesendet`,
        { variant: 'success' },
      );
    } catch (error) {
      const body = isAxiosError<PreisToolSaveRefusal>(error) ? error.response?.data : undefined;
      enqueueSnackbar(body?.message ?? 'Speichern fehlgeschlagen.', { variant: 'error' });
    }
  };

  return (
    <Group orientation="vertical" className="pt-main" id="preis-tool-v" defaultLayout={{ hitlist: 50, workspace: 50 }}>
      <Panel id="hitlist" minSize={94} className="pt-top">
        <FilterToolbar
          query={query}
          setQuery={setQuery}
          selectedMfgs={selectedMfgs}
          setSelectedMfgs={setSelectedMfgs}
          selectedGroups={selectedGroups}
          setSelectedGroups={setSelectedGroups}
          manufacturers={facets?.manufacturers ?? []}
          itemGroups={facets?.itemGroups ?? []}
          onlyDemand={onlyDemand}
          setOnlyDemand={setOnlyDemand}
          onlyAnomalies={onlyAnomalies}
          setOnlyAnomalies={setOnlyAnomalies}
          onlyEdited={onlyEdited}
          setOnlyEdited={setOnlyEdited}
          onlyNotInSap={onlyNotInSap}
          setOnlyNotInSap={setOnlyNotInSap}
          priceFilter={priceFilter}
          setPriceFilter={setPriceFilter}
          total={data?.total ?? 0}
          filtered={rows.length}
          legend={
            <LegendFilter
              selected={legendStates}
              onToggle={toggleLegendState}
              counts={legendCounts}
              limits={legendLimits}
              thresholds={legendThresholds}
              canManage={canManage}
            />
          }
          actions={
            canManage && (
              <DsButton size="small" onClick={() => setPriceListImportOpen(true)}>
                Preisliste hochladen
              </DsButton>
            )
          }
        />
        <HitList
          rows={rows}
          total={data?.total ?? 0}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          sorting={sorting}
          onSortingChange={setSorting}
          isPending={isPending}
          drafts={drafts}
          onPriceCommit={onPriceCommit}
          activeCell={activeCell}
          onActiveCellChange={setActiveCell}
          rowStates={rowStates}
          legendStates={legendStates}
          lowMarginPct={lowMarginPct}
          canEdit={canEditPrices}
        />
        <StatusBar
          activeCell={activeCell}
          rows={rows}
          drafts={drafts}
          dirtyCount={dirtyCount}
          onDiscard={() => setDrafts({})}
          onSave={handleSave}
          isSaving={savePrices.isPending}
          transferCounts={transferCounts}
          writebackMode={writeback?.mode}
          lowMarginPct={lowMarginPct}
          canEditPrices={canEditPrices}
          onResolveFailed={resolveFailedPrice}
        />
      </Panel>
      <Separator className="pt-resize-handle" />
      <Panel id="workspace" minSize={20} className="pt-bottom">
        <Workspace
          article={selected}
          basket={basket}
          setBasket={setBasket}
          hasSupplierDeal={selected ? (rowStates.get(selected.id)?.includes('deal') ?? false) : false}
          canManage={canManage}
        />
        <RightRail
          anomalies={supplierAnomalies?.entries ?? []}
          isLoading={anomaliesLoading}
          minSavingsPct={supplierAnomalies?.minSavingsPct ?? dealMinSavingsPct}
          setSelectedId={setSelectedId}
          basket={basket}
          setBasket={setBasket}
          articles={data?.data ?? []}
        />
        {priceListImportOpen && <PriceListImportDialog onClose={() => setPriceListImportOpen(false)} />}
      </Panel>
    </Group>
  );
}

function StatusBar({
  activeCell,
  rows,
  drafts,
  dirtyCount,
  onDiscard,
  onSave,
  isSaving,
  transferCounts,
  writebackMode,
  lowMarginPct,
  canEditPrices,
  onResolveFailed,
}: {
  activeCell: ActiveCell | null;
  rows: PreisToolItem[];
  drafts: PriceDrafts;
  dirtyCount: number;
  onDiscard: () => void;
  onSave: () => void;
  isSaving: boolean;
  transferCounts: { pending: number; failed: number };
  writebackMode: PreisToolSapWritebackMode | undefined;
  lowMarginPct: number;
  canEditPrices: boolean;
  onResolveFailed: (itemId: string, field: PriceField, price: number, keepSapPrice: boolean) => void;
}) {
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const item = activeCell ? rows[activeCell.rowIndex] : null;
  const field = activeCell?.colId as PriceField | undefined;
  const isPrice = field && ['standardPrice', 'itscopePrice', 'onlinerPrice'].includes(field);

  const original = item && isPrice ? item[field!] : null;
  const draft = item && isPrice ? drafts[item.id]?.[field!] : undefined;
  const current = draft !== undefined ? draft : original;
  const hasDraft = draft !== undefined && draft !== original;
  const openChange = item && isPrice ? item.openPriceChanges?.[field!] : undefined;
  const hint = writebackHint(writebackMode);

  const diff = hasDraft && current != null && original != null ? current - original : null;
  const diffPct = hasDraft && original != null && original !== 0 && current != null
    ? (current - original) / original
    : null;

  const mek = item?.movingAveragePrice ?? 0;
  const currentMargin = marginPct(current, mek);
  const currentCls = marginClass(currentMargin, lowMarginPct);

  return (
    <div className="pt-save-bar">
      <div className="changes" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {item && isPrice ? (
          <>
            <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-2)', fontSize: 'var(--fs-xs)' }}>
              {item.id}
            </span>
            <span style={{ fontWeight: 500 }}>{item.name}</span>
            <span style={{ color: 'var(--ink-3)' }}>·</span>
            <span style={{ fontWeight: 600 }}>{channelLabel(field!)}</span>
            {current != null && (
              <>
                <span style={{ fontFamily: 'var(--font-mono)' }}>{fmtEUR(current)}</span>
                <span className={`ds-pill ${currentCls}`} style={{ height: 15.5 }}>
                  {currentMargin != null ? (currentMargin >= 0 ? '+' : '') + fmtPct(currentMargin, 1) : '—'}
                </span>
              </>
            )}
            {hasDraft && original != null && (
              <>
                <span style={{ color: 'var(--ink-3)' }}>·</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>
                  vorher {fmtEUR(original)}
                </span>
                {diff != null && (
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: diff > 0 ? 'var(--pos)' : diff < 0 ? 'var(--neg)' : 'var(--ink-3)' }}>
                    {diff > 0 ? '+' : ''}{fmtEUR(diff)}
                  </span>
                )}
                {diffPct != null && (
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: diffPct > 0 ? 'var(--pos)' : diffPct < 0 ? 'var(--neg)' : 'var(--ink-3)' }}>
                    ({diffPct > 0 ? '+' : ''}{fmtPct(diffPct, 1)})
                  </span>
                )}
              </>
            )}
            {!hasDraft && openChange && (
              <>
                <span className={`ds-flag ${openChange.status === 'FAILED' ? 'critical' : 'warn'}`}>
                  {openChange.status === 'FAILED' ? 'Übertragung fehlgeschlagen' : 'nicht in SAP'}
                </span>
                <span
                  style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}
                  title={openChange.errorMessage ?? undefined}
                >
                  {openChange.status === 'FAILED'
                    ? `gewünscht ${fmtEUR(openChange.targetPrice)}${openChange.errorMessage ? ` · ${openChange.errorMessage}` : ''}`
                    : `SAP-Preis ${fmtEUR(openChange.sapPrice)}`}
                </span>
                {openChange.status === 'FAILED' && canEditPrices && writebackMode !== 'disabled' && (
                  <>
                    <DsButton
                      size="small"
                      disabled={isSaving}
                      title={`€ ${fmtEUR(openChange.targetPrice)} noch einmal an SAP senden`}
                      onClick={() => onResolveFailed(item.id, field!, openChange.targetPrice, false)}
                    >
                      Erneut senden
                    </DsButton>
                    {openChange.sapPrice != null && openChange.sapPrice > 0 && (
                      <DsButton
                        size="small"
                        variant="ghost"
                        disabled={isSaving}
                        title={`Den Preis aus SAP (€ ${fmtEUR(openChange.sapPrice)}) behalten, € ${fmtEUR(openChange.targetPrice)} wird nicht gesendet`}
                        onClick={() => onResolveFailed(item.id, field!, openChange.sapPrice!, true)}
                      >
                        SAP-Preis behalten
                      </DsButton>
                    )}
                  </>
                )}
              </>
            )}
          </>
        ) : dirtyCount > 0 ? (
          <span><b>{dirtyCount}</b> Preis(e) geändert · nicht gespeichert</span>
        ) : (
          <span style={{ color: 'var(--ink-3)' }}>Doppelklick oder Pfeiltasten zum Editieren</span>
        )}
      </div>
      <div className="actions" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {(transferCounts.pending > 0 || transferCounts.failed > 0) && (
          <span className="transfer-summary" title={hint ?? undefined}>
            Nicht in SAP: {transferCounts.pending} offen
            {transferCounts.failed > 0 && (
              <span style={{ color: 'var(--neg)' }}> · {transferCounts.failed} fehlgeschlagen</span>
            )}
          </span>
        )}
        {hint && (
          <span className="ds-flag info" title={hint}>
            {writebackMode === 'disabled' ? 'SAP-Übertragung nicht angebunden' : 'SAP-Übertragung simuliert'}
          </span>
        )}
        {dirtyCount > 0 && (
          <>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--ink-2)', marginRight: 4 }}>
              {dirtyCount} Änderung{dirtyCount !== 1 ? 'en' : ''}
            </span>
            <DsButton onClick={() => setConfirmDiscard(true)}>Verwerfen</DsButton>
            <DsButton variant="primary" onClick={onSave} disabled={isSaving}>
              Speichern
            </DsButton>
          </>
        )}
      </div>
      <DsModal
        open={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        title="Änderungen verwerfen?"
        description={`${dirtyCount} ungespeicherte Preisänderung${dirtyCount !== 1 ? 'en' : ''} ${dirtyCount !== 1 ? 'werden' : 'wird'} unwiderruflich verworfen.`}
        footer={
          <>
            <DsButton onClick={() => setConfirmDiscard(false)}>Abbrechen</DsButton>
            <DsButton variant="primary" onClick={() => { setConfirmDiscard(false); onDiscard(); }}>
              Verwerfen
            </DsButton>
          </>
        }
      >
        <span />
      </DsModal>
    </div>
  );
}
